"use client";

import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { useGlobalModalContext } from "@components/GlobalModalProvider/GlobalModalProvider";
import Modal from "@components/ui/modals/Modal";
import ModalContent from "@components/ui/modals/ModalContent";
import { Heading } from "@ecco/ui/heading";
import type { ModalProps } from "@ecco/ui/modal";
import { ModalBody, ModalOverlay } from "@ecco/ui/modal";
import type { StyleProps } from "@ecco/ui/types";
import type { Document } from "@contentful/rich-text-types";
import { Box } from "@ecco/ui/box";
import { Button } from "@ecco/ui/button";
import { Text } from "@ecco/ui/text";
import { ECCOIcon } from "@ecco/ui/ecco-icon";

import type { PropsWithChildren } from "react";
import { useCallback } from "react";
import { useAuthFormStore } from "@store/authFormStore";
import { styles } from "./BaseModal.styles";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import CommonCMS from "@lib/constants/contentful/cms-common";

type CustomStyle = {
    content: StyleProps;
};

type Props = {
    description?: string;
    label: string;
    customStyle?: CustomStyle;
    returnFocusOnClose?: ModalProps["returnFocusOnClose"];
    isOpen?: ModalProps["isOpen"];
    descriptionRichText?: Document;
    onClose?: () => void;
    backButtonLabel?: string;
} & PropsWithChildren;

export default function BaseModal({
    description,
    label,
    children,
    customStyle,
    descriptionRichText,
    isOpen,
    onClose,
    returnFocusOnClose,
    backButtonLabel,
}: Props) {
    const { hideModal, showModal } = useGlobalModalContext();
    const { returnModalType, setReturnModal } = useAuthFormStore();
    const { get } = useMicrocopy();

    const handleClose = useCallback(() => {
        setReturnModal(null);
        hideModal();
    }, [hideModal, setReturnModal]);

    const handleBack = useCallback(() => {
        if (returnModalType) {
            const returnTo = returnModalType;
            setReturnModal(null);
            showModal(returnTo);
        }
    }, [showModal, returnModalType, setReturnModal]);

    const showBackButton = Boolean(returnModalType);
    const buttonLabel = backButtonLabel || get(CommonCMS.shared, CommonCMS.backText) || "Back";

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose ?? handleClose}
            closeOnOverlayClick={true}
            returnFocusOnClose={returnFocusOnClose}
        >
            <ModalOverlay />
            <ModalContent {...styles.modalContent} {...(customStyle?.content ?? {})}>
                <Heading size="h5" sx={styles.heading}>
                    {label}
                </Heading>
                <ModalBody sx={styles.modalBody}>
                    {description}
                    {children}
                    {descriptionRichText && <RichText data={descriptionRichText} />}
                </ModalBody>
                {showBackButton && (
                    <Box sx={styles.backButtonContainer}>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={handleBack}
                            data-testid="modal-back-button"
                            sx={styles.backButton}
                        >
                            <ECCOIcon name="arrow-back" boxSize="6" />
                            <Text as="span" {...styles.backButtonText}>
                                {buttonLabel}
                            </Text>
                        </Button>
                    </Box>
                )}
            </ModalContent>
        </Modal>
    );
}
